// LMBDCForm.js
import React, { useState, useMemo, useEffect } from "react";
import { useEtatGlobal } from "../../../EtatGlobal.js";
import { User, Building2, ChevronDown, Check, GraduationCap } from "lucide-react";
import { Input } from "../../../../components/ui/input.js";
import { Textarea } from "../../../../components/ui/textArea.js";
import CabinetSelect from "../../components/CabinetSelect";
import SelectOuAutre from "../../components/SelectOuAutre";
import { CIVILITE_OPTIONS } from "../../components/lmSelectOptions";

// Thèmes proposés pour l'atelier "Réforme de la facturation électronique" —
// tous apparaissent sur le bon de commande (plus une sélection unique par
// liste déroulante), dans un champ libre modifiable si besoin.
const THEME_OPTIONS = [
  "Calendrier de la réforme, périmètre, acteurs et enjeux",
  "Comprendre : facture électronique, e-invoicing, e-reporting, PDP, PA",
  "Elaborer un succes plan pour déployer la facturation électronique au sein de son organisation",
  "Responsabilité du dirigeant, risque de non conformité, bonnes pratiques",
];

const ALL_THEMES_TEXT = THEME_OPTIONS.map((theme) => `✓ ${theme}`).join("\n");

// Offres d'atelier proposées — pour l'instant une seule, d'autres pourront
// être ajoutées ici plus tard (chacune avec son propre tarif et ses propres
// valeurs par défaut). Le choix de l'offre détermine "Intitulé de
// l'atelier", qui n'est donc jamais modifiable directement dans le
// formulaire — seulement via la sélection de l'offre.
const ATELIER_OFFERS = [
  {
    intitule: "Réforme de la facturation électronique",
    date_atelier: "14/09/2026",
    duree_atelier: "6 h (de 9h00 à 12h00 et de 13h00 à 16h00)",
    lieu_atelier: "19 chemin du Château d'Eau, local - 97410 SAINT-PIERRE",
    theme_atelier: ALL_THEMES_TEXT,
    tarif_atelier: "275,00",
    // Taux de TVA affiché dans le Word ("TVA ({tvap}%)"), distinct du montant
    // de TVA (tva_atelier, en €) — modifiable si le taux change.
    tvap: "8,5",
    tva_atelier: "23,37",
    ttc_atelier: "298,37",
    // Nombre de participants ("{participant}" dans le Word) — chiffres entiers uniquement.
    participant: "1",
  },
];

function LMBDCForm({ handleInputChange, setDetailsMission, setDetailsHonoraire }) {
  const [isOpen, setIsOpen] = useState(true);
  const [visible1, setVisible1] = useState(true);

  const { formData, setFormData } = useEtatGlobal();

  // Pré-sélection de la première offre d'atelier au premier affichage du
  // formulaire, si aucune offre n'a encore été choisie (nouveau BDC ou
  // brouillon antérieur à l'ajout de ce type de LM) — remplit aussi tous
  // les champs du dessous par défaut.
  useEffect(() => {
    const offreParDefaut = ATELIER_OFFERS[0];

    if (!formData.intitule_atelier || formData.intitule_atelier.trim() === "") {
      setFormData((prev) => ({
        ...prev,
        intitule_atelier: offreParDefaut.intitule,
        date_atelier: offreParDefaut.date_atelier,
        duree_atelier: offreParDefaut.duree_atelier,
        lieu_atelier: offreParDefaut.lieu_atelier,
        theme_atelier: offreParDefaut.theme_atelier,
        tarif_atelier: offreParDefaut.tarif_atelier,
        tvap: offreParDefaut.tvap,
        tva_atelier: offreParDefaut.tva_atelier,
        ttc_atelier: offreParDefaut.ttc_atelier,
        participant: offreParDefaut.participant,
      }));
      return;
    }

    // Brouillon créé avant l'ajout de "tvap"/"participant" : intitule_atelier
    // (et le reste) sont déjà renseignés, donc le bloc ci-dessus ne se
    // redéclenche jamais — sans ça, ces deux champs resteraient undefined
    // (et s'afficheraient tels quels dans le Word) pour tout brouillon en
    // cours commencé avant ce jour.
    if (!formData.tvap || !formData.participant) {
      setFormData((prev) => ({
        ...prev,
        tvap: prev.tvap || offreParDefaut.tvap,
        participant: prev.participant || offreParDefaut.participant,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOffreChange = (offre) => {
    setFormData((prev) => ({
      ...prev,
      intitule_atelier: offre.intitule,
      date_atelier: offre.date_atelier,
      duree_atelier: offre.duree_atelier,
      lieu_atelier: offre.lieu_atelier,
      theme_atelier: offre.theme_atelier,
      tarif_atelier: offre.tarif_atelier,
      tvap: offre.tvap,
      tva_atelier: offre.tva_atelier,
      ttc_atelier: offre.ttc_atelier,
      participant: offre.participant,
    }));
  };

  // Chiffres entiers uniquement — tout le reste (lettres, virgule, espace) est
  // filtré au fil de la saisie plutôt que rejeté après coup.
  const handleParticipantChange = (e) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
    setFormData((prev) => ({ ...prev, participant: digitsOnly }));
  };

  const isValid = useMemo(() => {
    // "adresse" n'est pas obligatoire — nullable, laissée vide dans le
    // document généré si non renseignée.
    const requiredFields = [
      "nom_client",
      "lieu_collab",
      "nom",
      "prenom",
      "email",
      "telephone",
    ];

    return requiredFields.every((field) => {
      const value = formData[field];
      return (
        value !== " " && value !== null && value !== undefined && value !== ""
      );
    });
  }, [formData]);

  const isValidAtelier = useMemo(() => {
    const requiredFields = [
      "intitule_atelier",
      "date_atelier",
      "duree_atelier",
      "lieu_atelier",
      "theme_atelier",
      "tarif_atelier",
      "tvap",
      "tva_atelier",
      "ttc_atelier",
      "participant",
    ];

    return requiredFields.every((field) => {
      const value = formData[field];
      return (
        value !== " " && value !== null && value !== undefined && value !== ""
      );
    });
  }, [formData]);

  useEffect(() => {
    setDetailsMission(isValid);
  }, [isValid, setDetailsMission]);

  useEffect(() => {
    setDetailsHonoraire(isValidAtelier);
  }, [isValidAtelier, setDetailsHonoraire]);

  // Style commun aux tuiles d'offre — repris de LM BG Logiciel.
  const tuileStyle = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#840040",
    color: "white",
    padding: "10px",
    paddingRight: "35px",
    borderRadius: "15px",
    justifyContent: "start",
    width: "45%",
    textAlign: "start",
    boxSizing: "border-box",
  };

  return (
    <div className="Container">
      <div>
        <div
          className="border-2 border-slate-200 rounded-xl bg-white overflow-hidden"
          style={{ padding: "10px" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            className="p-4 hover:bg-slate-50 transition-colors rounded-lg mb-5"
            onClick={() => setIsOpen(!isOpen)}
          >
            <div
              style={{
                display: "flex",
                gap: "25px",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                  isValid
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {isValid ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Building2 className="w-5 h-5" />
                )}
              </div>

              <h3
                className="font-semibold text-slate-800"
                style={{ margin: 0 }}
              >
                1. Votre entité
              </h3>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-slate-400 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
          {isOpen && (
            <div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-6 pt-0 space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-slate-600 mb-1 block text-xs">
                      Nom du client
                    </label>
                    <Input
                      type="text"
                      name="nom_client"
                      value={formData.nom_client}
                      onChange={handleInputChange}
                      required
                      className="bg-white border-slate-200 h-9 text-sm"
                    />
                  </div>
                  <CabinetSelect />
                  <div>
                    <label className="text-slate-600 mb-1 block text-xs">
                      Adresse
                    </label>
                    <Input
                      type="text"
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleInputChange}
                      className="bg-white border-slate-200 h-9 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-slate-600 mb-1 block text-xs">
                      SIREN
                    </label>
                    <Input
                      type="text"
                      name="siren"
                      value={formData.siren}
                      maxLength="9"
                      onChange={handleInputChange}
                      className="bg-white border-slate-200 h-9 text-sm"
                    />
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-700 mb-5 text-sm flex items-center gap-2">
                    <User className="w-4 h-4 text-[#8B1D41]" />
                    Contact Référent — Signataire
                  </h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-slate-600 mb-1 block text-xs">
                          Civilité
                        </label>
                        <SelectOuAutre
                          name="civilite_lm"
                          value={formData.civilite_lm}
                          onChange={handleInputChange}
                          options={CIVILITE_OPTIONS}
                        />
                      </div>
                      <div>
                        <label className="text-slate-600 mb-1 block text-xs">
                          Nom
                        </label>
                        <Input
                          type="text"
                          name="nom"
                          value={formData.nom}
                          onChange={handleInputChange}
                          className="bg-white border-slate-200 h-9 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-slate-600 mb-1 block text-xs">
                        Prénom
                      </label>
                      <Input
                        type="text"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleInputChange}
                        className="bg-white border-slate-200 h-9 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-slate-600 mb-1 block text-xs">
                        Email
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="bg-white border-slate-200 h-9 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-slate-600 mb-1 block text-xs">
                        Téléphone
                      </label>
                      <Input
                        type="text"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleInputChange}
                        className="bg-white border-slate-200 h-9 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <br />
      <div
        className="border-2 border-slate-200 rounded-xl bg-white overflow-hidden"
        style={{ padding: "10px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          className="p-4 hover:bg-slate-50 transition-colors rounded-lg mb-5"
          onClick={() => setVisible1(!visible1)}
        >
          <div
            style={{
              display: "flex",
              gap: "25px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                isValidAtelier
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {isValidAtelier ? (
                <Check className="w-5 h-5" />
              ) : (
                <GraduationCap className="w-5 h-5" />
              )}
            </div>
            <h3 className="font-semibold text-slate-800" style={{ margin: 0 }}>
              2. Atelier
            </h3>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-slate-400 transition-transform ${
              visible1 ? "rotate-180" : ""
            }`}
          />
        </div>
        {visible1 && (
          <div className="p-6 pt-0 space-y-6">
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 text-sm">
                Offre d'atelier
              </h4>
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "stretch",
                }}
              >
                {ATELIER_OFFERS.map((offre) => (
                  <div key={offre.intitule} style={tuileStyle}>
                    <input
                      style={{
                        width: "20px",
                        height: "20px",
                        marginRight: "25px",
                        marginLeft: "25px",
                      }}
                      type="radio"
                      name="intitule_atelier_offre"
                      value={offre.intitule}
                      checked={
                        formData.intitule_atelier === offre.intitule ||
                        !formData.intitule_atelier
                      }
                      onChange={() => handleOffreChange(offre)}
                    />
                    <div style={{ width: "100%" }}>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: "bold",
                          marginBottom: "5px",
                        }}
                      >
                        {offre.intitule}
                      </p>
                      <p style={{ margin: 0, fontSize: "10px" }}>
                        tarif de {offre.tarif_atelier} € HT
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-slate-600 mb-1 block text-xs">
                Intitulé de l'atelier
              </label>
              <Input
                type="text"
                value={formData.intitule_atelier || ""}
                disabled
                className="bg-slate-100 border-slate-200 h-9 text-sm text-slate-500"
              />
              <p className="text-[11px] text-slate-400 mt-1 m-0">
                Déterminé par l'offre sélectionnée ci-dessus.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="text-slate-600 mb-1 block text-xs">
                  Date de l'atelier
                </label>
                <Input
                  type="text"
                  name="date_atelier"
                  value={formData.date_atelier || ""}
                  onChange={handleInputChange}
                  placeholder="JJ/MM/AAAA"
                  className="bg-white border-slate-200 h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-slate-600 mb-1 block text-xs">
                  Nombre de participants
                </label>
                <Input
                  type="text"
                  inputMode="numeric"
                  name="participant"
                  value={formData.participant || ""}
                  onChange={handleParticipantChange}
                  className="bg-white border-slate-200 h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-slate-600 mb-1 block text-xs">
                  Durée
                </label>
                <Input
                  type="text"
                  name="duree_atelier"
                  value={formData.duree_atelier || ""}
                  onChange={handleInputChange}
                  className="bg-white border-slate-200 h-9 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="text-slate-600 mb-1 block text-xs">
                Lieu de l'atelier
              </label>
              <Input
                type="text"
                name="lieu_atelier"
                value={formData.lieu_atelier || ""}
                onChange={handleInputChange}
                className="bg-white border-slate-200 h-9 text-sm"
              />
            </div>

            <div>
              <label className="text-slate-600 mb-1 block text-xs">
                Thèmes abordés
              </label>
              <Textarea
                name="theme_atelier"
                value={formData.theme_atelier || ""}
                onChange={handleInputChange}
                rows={6}
                className="bg-white border-slate-200 text-sm"
              />
              <p className="text-[11px] text-slate-400 mt-1 m-0">
                Tous les thèmes de l'atelier apparaissent ici — modifiable si besoin.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-slate-600 mb-1 block text-xs">
                  Tarif HT (€)
                </label>
                <Input
                  type="text"
                  name="tarif_atelier"
                  value={formData.tarif_atelier || ""}
                  onChange={handleInputChange}
                  className="bg-white border-slate-200 h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-slate-600 mb-1 block text-xs">
                  Taux de TVA (%)
                </label>
                <Input
                  type="text"
                  name="tvap"
                  value={formData.tvap || ""}
                  onChange={handleInputChange}
                  className="bg-white border-slate-200 h-9 text-sm"
                />
                <p className="text-[11px] text-slate-400 mt-1 m-0">
                  Affiché dans le Word : "TVA ({formData.tvap || "8,5"}%)".
                </p>
              </div>
              <div>
                <label className="text-slate-600 mb-1 block text-xs">
                  TVA ({formData.tvap || "8,5"} %) (€)
                </label>
                <Input
                  type="text"
                  name="tva_atelier"
                  value={formData.tva_atelier || ""}
                  onChange={handleInputChange}
                  className="bg-white border-slate-200 h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-slate-600 mb-1 block text-xs">
                  TTC (€)
                </label>
                <Input
                  type="text"
                  name="ttc_atelier"
                  value={formData.ttc_atelier || ""}
                  onChange={handleInputChange}
                  className="bg-white border-slate-200 h-9 text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LMBDCForm;
